'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Lock, Sparkles, Loader2, CheckCircle, ChevronDown } from 'lucide-react';

const CATEGORIES = ['Amor', 'Trabajo', 'Familia', 'Vecinos', 'Famosos', 'Otro'];
const MAX_DESC = 600;
const MAX_TITLE = 80;

interface FormState {
  title: string;
  description: string;
  category: string;
}

const EMPTY: FormState = { title: '', description: '', category: '' };

export default function SubmitGossipPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const descRemaining = MAX_DESC - form.description.length;
  const titleRemaining = MAX_TITLE - form.title.length;

  function validate(): boolean {
    const newErrors: Partial<FormState> = {};
    if (!form.title.trim()) newErrors.title = 'El título es obligatorio 😬';
    else if (form.title.trim().length < 5) newErrors.title = 'Muy corto, dale más detalle';
    if (!form.description.trim()) newErrors.description = 'Cuéntanos el chisme completo 👀';
    else if (form.description.trim().length < 20) newErrors.description = '¡Muy corto! Necesitamos más drama';
    if (!form.category) newErrors.category = 'Elige una categoría';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // BACKEND: POST /api/chismes — create anonymous gossip submission, add to host queue
    await new Promise((r) => setTimeout(r, 1400));
    setSubmitting(false);
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <AppLayout>
        <div className="min-h-[80vh] flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center bg-card border border-primary/20 rounded-3xl p-10 shadow-lg">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-extrabold text-foreground mb-2">¡Chisme enviado!</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Tu chisme ya está en la cola de la anfitriona. La comunidad votará y si queda top, ¡lo cuenta en vivo mientras teje! 🧶🔥
            </p>
            <div className="flex items-center justify-center gap-2 text-green-600 text-sm font-semibold mb-6">
              <CheckCircle size={16} />
              Tu chisme es completamente anónimo
            </div>
            <button
              onClick={() => { setForm(EMPTY); setErrors({}); setSubmitted(false); }}
              className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold w-full"
            >
              ¿Tienes otro chisme? 🤭
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8 text-center">
          <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
            Anónimo & Seguro 🔒
          </span>
          <h1 className="text-3xl font-extrabold text-foreground mb-2">
            Suelta el Chisme 🧶
          </h1>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
            Envía tu chisme de forma anónima. La anfitriona lo contará en vivo mientras teje si la comunidad lo vota.
          </p>
        </div>

        {/* Privacy notice */}
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <Lock size={16} className="text-green-600 mt-0.5 shrink-0" />
          <p className="text-xs text-green-700 leading-relaxed">
            <span className="font-semibold">100% anónimo.</span> No guardamos tu IP ni datos personales. Nadie sabrá que fuiste tú. 🔒
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-3xl p-8 shadow-sm flex flex-col gap-6">
          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-foreground">
                Título del chisme <span className="text-primary">*</span>
              </label>
              <span className={`text-xs font-medium tabular-nums ${titleRemaining < 15 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {titleRemaining} restantes
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Un resumen corto y jugoso 👀</p>
            <input
              type="text"
              value={form.title}
              maxLength={MAX_TITLE}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ej: Mi vecina tiene dos maridos y ninguno lo sabe..."
              className={`w-full bg-input border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${errors.title ? 'border-red-400' : 'border-border'}`}
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
          </div>

          {/* Category dropdown */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Categoría <span className="text-primary">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">Ayuda a organizar los chismes en la cola</p>
            <div className="relative">
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className={`w-full appearance-none bg-input border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition pr-10 ${errors.category ? 'border-red-400' : 'border-border'} ${!form.category ? 'text-muted-foreground' : ''}`}
              >
                <option value="" disabled>Selecciona una categoría...</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-foreground">
                El chisme completo <span className="text-primary">*</span>
              </label>
              <span className={`text-xs font-medium tabular-nums ${descRemaining < 80 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {descRemaining} restantes
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Cuéntalo con todos los detalles. Más dramático, mejor. 🎭</p>
            <textarea
              value={form.description}
              maxLength={MAX_DESC}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={6}
              placeholder="Resulta que mi vecina del 3er piso..."
              className={`w-full bg-input border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none transition ${errors.description ? 'border-red-400' : 'border-border'}`}
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-base disabled:opacity-60 transition"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Enviando chisme...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                ¡Soltar el Chisme a la Cola! 🧶
              </>
            )}
          </button>
        </form>

        {/* Footer note */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Los chismes con más votos de la comunidad son los primeros en la cola de la anfitriona 🏆
        </p>
      </div>
    </AppLayout>
  );
}
